// @flow strict-local

import type {EnvState} from 'src/action-creators/env';
import logger from 'src/utils/logger';

// $FlowFixMe[untyped-import]
import * as sentry from 'sentry';


const DSN_URL = 'https://05107e14b96c467489873f093f7399ba@sentry.io/190397';
let webReplayInitialized = false;
let extensionReplayInitialized = false;

const denyUrls = [
  /recaptcha/i,
  /walkme/i,
  /chameleon/i,
  /delighted/i,
  /^chrome:\/\//i,
  /candu\.app/i,
  /cdn\.candu\.app/i,
  /maze\.design/i,
  /cdn\.maze\.design/i,
  /widget\.maze\.design/i,
  /s3\.maze\.design/i,
  /clarity\.ms/i,
  /clarity-extended\.js/i,
  /clarity\.js/i,
  /clarity\.io/i,
];

const IGNORED_ERRORS = [
  // start

  // NOTE (kyle): this error notifies of perf issues. it is benign.
  {
    message: 'ResizeObserver loop limit exceeded',
    exactMatch: true, // exact match for error message
  },
  // end

  {
    message: 'AbortError: Aborted',
    exactMatch: true,
  },
  //start

  // NOTE (kyle): filter out uncaught Recaptcha exceptions
  {
    message: 'Timeout',
    exactMatch: true,
  },
  //end

  //start
  // NOTE (kyle): filter out Delighted errors
  {
    message: 'Synchronous XHR',
    exactMatch: false, // partial match (substring)
  },
  {
    message: 'web.delighted.com',
    exactMatch: false,
  },
  // end

  //start
  // NOTE(marcos): filter out two draft errors caused by browser plugins modifying contenteditable
  {
    message: 'Failed to execute \'extend\' on \'Selection\'',
    exactMatch: false,
  },
  {
    message: 'Failed to execute \'setStart\' on \'Range\'',
    exactMatch: false,
  },
  // end

  // walkme's media player can error out but we can only see it in the stack
  // q.v. https://sentry.io/share/issue/36b5b8a1685d4f63a2c8dbb0177f9580/
  {
    message: 'walkme_lib',
    exactMatch: false,
    inStack: true, // search in stack trace rather than message
  },

  // no-op generated from sentry's helper.js
  // node_modules/@sentry/browser/esm/helpers.js in sentryWrapped at line 72:23
  {
    message: 't.apply is not a function',
    pathName: '@sentry/browser/esm/helpers.js',
    exactMatch: false,
    inStack: true,
  },
  // null is not an object (evaluating 'u.contentWindow.postMessage')
  // node_modules/@sentry/browser/esm/helpers.js in sentryWrapped at line 72:23
  {
    message: 'null is not an object (evaluating \'u.contentWindow.postMessage\')',
    pathName: '@sentry/browser/esm/helpers.js',
    exactMatch: false,
    inStack: true,
  },

  // Cannot read properties of undefined (reading 'getCharacterList')
  // node_modules/draft-js/lib/removeEntitiesAtEdges.js in removeForBlock at line 93:21
  {
    message: 'Cannot read properties of undefined (reading \'getCharacterList\')',
    pathName: 'draft-js/lib/removeEntitiesAtEdges.js',
    exactMatch: false,
    inStack: true,
  },

  // start

  // messaging errors
  {
    message: 'The browser is shutting down',
    exactMatch: false,
  },
  {
    message: 'ApiError - 403 /api/v3/analytics/messaging/overview',
    exactMatch: false,
  },
  {
    message: 'ApiError - 403 /api/v3/analytics/messaging/members',
    exactMatch: false,
  },
  {
    message: 'ApiError - 403 /api/v3/analytics/messaging/trend',
    exactMatch: false,
  },
  {
    message: 'Could not establish connection. Receiving end does not exist.',
    exactMatch: false,
  },
  {
    message: 'No Listener: pcp_refresh',
    exactMatch: false,
    inStack: true,
    pathName: '@webkit-masked-url',
  },
  // end

  {
    message:
      'Failed to execute \'removeChild\' on \'Node\': The node to be removed is not a child of this node.',
    exactMatch: false,
  },

  {
    message: 'ResizeObserver loop completed with undelivered notifications.',
    exactMatch: false,
  },
  {
    message: 'Attempted to hide a menu that was not already being displayed.',
    exactMatch: false,
    inStack: true,
  },
  {
    message:
      'NotAllowedError: play() failed because the user didn\'t interact with the document first.',
    exactMatch: false,
    inStack: true,
  },
  // Chameleon assets is external library which is causing this error
  {
    message: 'Invalid regular expression: invalid group specifier name',
    pathName: '/assets/chmln/',
    exactMatch: false,
    inStack: true,
  },
  // Chameleon script is external script which is causing this error
  {
    message: `TypeError: Cannot read properties of undefined (reading 'join')`,
    pathName: 'chmln.js',
    exactMatch: false,
    inStack: true,
  },
  // Clarity script is external script which is causing this error
  {
    message: `TypeError: Cannot read properties of undefined (reading 'latency')`,
    pathName: 'clarity-extended.js',
    exactMatch: false,
    inStack: true,
  },
  {
    message: 'undefined is not an object (evaluating \'i.messages.join\')',
    pathName: '/assets/chmln/',
    exactMatch: false,
    inStack: true,
  },
  // Sentry message triggered for unlisted timezone. Add to list if valid. silencing because Etc/Unknown is not valid
  {
    message: 'Invalid timezone: Etc/Unknown',
    exactMatch: true,
  },
  // Sentry message triggered for unlisted timezone. Add to list if valid. silencing because +00:00 is not valid
  {
    message: 'Invalid timezone: +00:00',
    exactMatch: true,
  },
  // The Brands API error is called by the "File Upload" app, but the error points to the unrelated referrer-portal file.
  {
    message: 'Error: ApiError - 401',
    pathName: 'utils/referrer-portal.js',
    exactMatch: false,
    inStack: true,
  },
  {
    message: 'Network request failed',
    exactMatch: false,
  },
  {
    // this is a permission issue, we cannot do anything about it
    message:
      'The play method is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',
    exactMatch: true,
  },
  {
    message: 'Object captured as promise rejection with keys',
    exactMatch: false,
    inStack: true,
  },
  {
    message: 'Cannot read properties of null (reading \'shadowRoot\')',
    exactMatch: false,
    inStack: true,
  },
  {
    message: 'Non-Error promise rejection',
    exactMatch: false,
    inStack: true,
  },

  {
    message: 'captured as promise rejection',
    exactMatch: false,
    inStack: true,
  },
  {
    message: 'Object captured as promise rejection with keys',
    exactMatch: false,
    inStack: true,
  },
];

function shouldIgnoreThirdPartyErrors(stackTraceFrames): boolean {
  const thirdPartyFileNames = [
    '@webkit-masked-url',
    '/assets/chmln/',
    'clarity-extended.js',
  ];

  return stackTraceFrames.some(({filename}) =>
    thirdPartyFileNames.some((tpFileName) => filename.includes(tpFileName)),
  );
}

function shouldIgnoreError(exception, stackTraceFrames, eventMessage): boolean {
  return IGNORED_ERRORS.some(({message, pathName, exactMatch, inStack}) => {
    const exceptionMessage =
      typeof exception === 'string' ? exception : exception?.message || '';

    if (inStack) {
      if (pathName) {
        const inStackFrame = stackTraceFrames.some(({filename}) =>
          filename.includes(pathName),
        );
        const inStack = exception?.stack?.includes(pathName);
        return (inStackFrame || inStack) && exception?.stack?.includes(message);
      }
      return exception?.stack?.includes(message);
    }

    if (exactMatch) {
      return exceptionMessage === message || eventMessage === message;
    }

    return (
      exceptionMessage.includes(message) ||
      (eventMessage && eventMessage.includes(message))
    );
  });
}

function beforeSend(event, hint) {
  const {originalException} = hint;
  const {exception, message: eventMessage} = event;
  const stackTraceFrames = exception?.values?.[0]?.stacktrace?.frames || [];

  if (!exception && !eventMessage) {
    return null;
  }

  if (stackTraceFrames?.length === 0) {
    return null;
  }

  if (shouldIgnoreThirdPartyErrors(stackTraceFrames)) {
    return null;
  }

  if (shouldIgnoreError(originalException, stackTraceFrames, eventMessage)) {
    return null;
  }

  try {
    const requestUrl = event?.request?.url || window?.location?.href;
    if (requestUrl && requestUrl.includes('/trm')) {
      return null;
    }
  } catch (error) {
    logger.error('Exception in suppressing TRM errors in sentry');
  }

  return event;
}
function shouldEnableReplay(
  environment: string,
  isExtension: boolean,
): boolean {
  //in prod, only enable replay if it's not already initialized
  return (
    (environment === 'production' || environment.includes('extension')) &&
    !(isExtension ? extensionReplayInitialized : webReplayInitialized)
  );
}

export function bootStrapSentryFromExtension(releaseName: string) {
  const integrations = [
    sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['sensehq-webapp', 'sensehq-extension'],
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
    }),
    shouldEnableReplay(releaseName, true) &&
      sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
  ];

  sentry.init({
    dsn: DSN_URL,
    environment: releaseName,
    beforeSend,
    integrations,
    denyUrls,
    ...(shouldEnableReplay(releaseName, true) && {
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.1,
    }),
  });

  extensionReplayInitialized = true;
}

// other products can use this
export function bootstrapSentry(env: EnvState) {
  const integrations = [
    sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['sensehq-webapp', 'sensehq-extension'],
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
    }),
    shouldEnableReplay(env.serverRuntimeEnvironment, false) &&
      sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
  ];

  sentry.init({
    dsn: DSN_URL,
    release: env.release || 'dev',
    environment: env.serverRuntimeEnvironment,
    serverName: env.hostname || 'dserver.com',
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'AbortError: Aborted',
      'TypeError: Network request failed',
    ],
    beforeSend,
    integrations,
    denyUrls,
    ...(shouldEnableReplay(env.serverRuntimeEnvironment, false) && {
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.1,
    }),
  });

  webReplayInitialized = true;
}
